// src/modules/app-views/welcome/pages/Init/modals/PendingInvitationsModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Flex, Modal, Spin } from 'antd';
import { CloseOutlined, /* LogoutOutlined, */ MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import axiosService from 'services/axiosService';
import authService from 'services/authService';
import appService from 'services/appService';
import InvitationCard from './InvitationCard';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PendingInvitationsModal = ({ show, setShow, invitations = [] }) => {
  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [decisions, setDecisions] = useState({});
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const allAnswered = useMemo(
    () => invitations.length > 0 && Object.keys(decisions).length === invitations.length,
    [decisions, invitations.length]
  );

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleDecision = useCallback((subscriptionId, decision) => {
    setDecisions(prev => {
      if (decision === null) {
        // Reset decision
        const next = { ...prev };
        delete next[subscriptionId];
        return next;
      }
      return { ...prev, [subscriptionId]: decision };
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!allAnswered || loading) return;

    setLoading(true);

    try {
      // Process each decision using existing backend routes
      for (const [subscriptionId, decision] of Object.entries(decisions)) {
        const action = decision === 'accepted' ? 'accept' : 'decline';

        await axiosService.post({
          url: `/v1/subscriptions/${subscriptionId}/invites/${action}`,
          headers: { 'x-resultasobject': true },
        });
      }

      // Single refresh after all decisions processed
      await authService.signInRefresh();

      // Modal closes because pendingInvitations will be empty after refresh
      setShow(false);
    } catch (err) {
      console.error('[PendingInvitationsModal] Error processing invitations:', err);
      appService.message({
        flag: 'error',
        text: 'error_processing_invitations',
      });
    } finally {
      setLoading(false);
    }
  }, [allAnswered, loading, decisions, setShow]);

  const handleClose = useCallback(() => {
    setShow(false);
  }, [setShow]);

  // const handleExitSystem = useCallback(() => {
  //   authService.signOut({ replace: true });
  // }, []);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      open={show}
      title={
        <Flex align="center" gap={8}>
          <MailOutlined />
          <span>{translateX('pending_invitations')}</span>
        </Flex>
      }
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={520}
      footer={
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
          <Button
            icon={<CloseOutlined />}
            onClick={handleClose}
            disabled={loading}
          >
            {translateX('close')}
          </Button>

          {/* <Button
            icon={<LogoutOutlined />}
            onClick={handleExitSystem}
            disabled={loading}
          >
            {translateX('exit_system')}
          </Button> */}

          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleConfirm}
            disabled={!allAnswered}
            loading={loading}
          >
            {translateX('confirm')}
          </Button>
        </Flex>
      }
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        title={translateX('pending_invitations_description')}
      />

      {loading && (
        <Flex justify="center" className="mb-3">
          <Spin />
        </Flex>
      )}

      <Flex vertical gap={12} style={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
        {invitations.map(inv => (
          <InvitationCard
            key={inv._id}
            invitation={inv}
            decision={decisions[inv._id] || null}
            onDecision={handleDecision}
            disabled={loading}
          />
        ))}
      </Flex>

      {!allAnswered && invitations.length > 0 && (
        <div
          className="mt-3"
          style={{ textAlign: 'center', fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}
        >
          {translateX('all_invitations_must_be_answered')}
        </div>
      )}
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PendingInvitationsModal;
